import { graphHooks } from '@tunasong/graph-lib/react'
import type { EntityVersion } from '@tunasong/models'
import { dayjs, logger } from '@tunasong/models'
import { useThunkDispatch } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isVersioned } from '@tunasong/schemas'
import { TunaEditorContext } from '@tunasong/plugin-lib'
import {
  useNavigate,
  Button,
  LinearProgress,
  ListItem,
  Tab,
  Tabs,
  List,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Typography,
  useIsOwner,
} from '@tunasong/ui-lib'
import type React from 'react'
import type { FC, MouseEvent } from 'react'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { HistoryChanges } from './history-changes.js'
import TimeTravelSelect from './history-timetravel.js'
import styles from './history.module.css'

export interface EntityHistoryProps {
  className?: string
  entity: Persisted<Entity>
  onComplete(): void
  /** The changes view opened or closed (GH #2695): the dialog goes full screen on phones while it is open */
  onChangesOpenChange?(open: boolean): void
}

export const EntityHistory: FC<EntityHistoryProps> = props => {
  const { className, entity, onComplete, onChangesOpenChange } = props

  const dispatch = useThunkDispatch()
  const navigate = useNavigate()
  /**
   * GH #2726: editors see the version history and Show changes. Restoring stays the owner's, so they get no Restore
   * and no Time Travel, whose only action is a restore.
   */
  const isOwner = useIsOwner(entity)
  const [history, setHistory] = useState<EntityVersion[]>([])
  /** The numbered versions the server holds; the entity's own version is the current state when it is not one */
  const [numbered, setNumbered] = useState<Set<number>>(() => new Set())
  /** GH #2695: the version whose changes are shown in place of the list */
  const [showing, setShowing] = useState<EntityVersion | null>(null)
  /** The open document, for the current state's changes */
  const editor = useContext(TunaEditorContext)
  const showChangesRefs = useRef(new Map<number, HTMLButtonElement>())
  const returnFocusTo = useRef<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [restoreEntityVersion] = graphHooks.useEntityRestoreVersion()
  const [restoreTimeTravel] = graphHooks.useEntityTimeTravelRestore()
  const [loadEntityHistory] = graphHooks.useLoadEntityHistory()

  useEffect(() => {
    if (!(entity.id && isVersioned(entity))) {
      return
    }

    setLoading(true)
    void loadEntityHistory({ id: entity.id }, true)
      .unwrap()
      .then(history => {
        /** Entity may or may not be part of the history. If it isn't, push it */
        const currentVersion = { id: entity.id, version: entity.version, versionAt: entity.versionAt }
        const all = [history.find(v => v.version === entity.version) ? null : currentVersion, ...history].filter(
          Boolean
        ) as EntityVersion[]
        const sorted = all.sort((a, b) => (a.version < b.version ? 1 : a.version === b.version ? 0 : -1))
        setNumbered(new Set(history.map(v => v.version)))
        setHistory(sorted)
      })
      .catch(e => logger.error(e))
      .finally(() => setLoading(false))
  }, [dispatch, entity, loadEntityHistory])

  const restoreVersion = (version: EntityVersion) => {
    setLoading(true)
    void restoreEntityVersion({ version })
      .unwrap()
      .then(() => {
        setLoading(false)
        onComplete()
      })
      .catch(e => logger.error(e))
  }

  const handleRestore = (version: EntityVersion) => (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    restoreVersion(version)
  }

  const handleTimetravel = useCallback(
    ({ entity, travelTo }: { entity: Persisted<Entity>; travelTo: string }) => {
      setLoading(true)
      void restoreTimeTravel({ id: entity.id, timeTravelTo: travelTo })
        .unwrap()
        .then(() => {
          setLoading(false)

          /** We need to refresh the page here to ensure that the editor starts a-fresh */
          void navigate(0)

          onComplete()
        })
        .catch(e => logger.error(e))
    },
    [navigate, onComplete, restoreTimeTravel]
  )

  const handleShowChanges = (version: EntityVersion) => {
    setShowing(version)
    onChangesOpenChange?.(true)
  }

  const handleBack = () => {
    returnFocusTo.current = showing?.version ?? null
    setShowing(null)
    onChangesOpenChange?.(false)
  }

  /** Back returns focus to the Show changes it came from (spec §1, §7) */
  useEffect(() => {
    if (showing || returnFocusTo.current === null) {
      return
    }
    showChangesRefs.current.get(returnFocusTo.current)?.focus()
    returnFocusTo.current = null
  }, [showing])

  /** The current state is compared with the open document; without one it cannot be */
  const liveContentOf = (version: EntityVersion) =>
    numbered.has(version.version) ? undefined : (editor?.children as unknown[] | undefined)

  const canShowChanges = (version: EntityVersion) => numbered.has(version.version) || Boolean(editor)

  const showingIndex = showing ? history.findIndex(v => v.version === showing.version) : -1
  const showingPrevious = showingIndex >= 0 ? history[showingIndex + 1] : undefined

  const [tab, setTab] = useState(isOwner ? 0 : 1)
  const handleTab = useCallback((_ev: React.SyntheticEvent | null, val: string | number) => setTab(val as number), [])

  return (
    <>
      <Tabs onChange={handleTab} value={tab}>
        {isOwner ? <Tab label="Time Travel" value={0} /> : null}
        <Tab label="Version History" value={1} disabled={!isVersioned(entity)} />
      </Tabs>
      {loading ? <LinearProgress /> : null}
      {tab === 0 && isOwner ? (
        <>
          <Alert title="Restore" severity="info" className={styles.alert}>
            Choose the date you want to restore {entity.name} to. Note that only the content of the {entity.type} will
            be restored, and not the metadata.
          </Alert>
          <TimeTravelSelect entity={entity} onChange={handleTimetravel} disableRestore={loading} />
        </>
      ) : null}
      {tab === 1 && isVersioned(entity) && showing && showingPrevious ? (
        <HistoryChanges
          entity={entity}
          version={showing}
          previous={showingPrevious}
          liveContent={liveContentOf(showing)}
          restoreDisabled={loading || showing.version === entity.version}
          onBack={handleBack}
          onRestore={isOwner ? restoreVersion : undefined}
        />
      ) : null}
      {tab === 1 && isVersioned(entity) && !(showing && showingPrevious) ? (
        <>
          <Typography variant="body2" className={styles.description}>
            When you restore an old version, the old version content will be copied onto the new version.{' '}
          </Typography>

          <List className={className}>
            {history.map((version, idx) => (
              <ListItem key={idx}>
                <ListItemText>{version.version}</ListItemText>
                <ListItemText>{dayjs(version.versionAt).format('LLL')}</ListItemText>
                <ListItemSecondaryAction>
                  {idx === history.length - 1 ? (
                    <Typography variant="body2" color="textSecondary" component="span">
                      First version
                    </Typography>
                  ) : canShowChanges(version) ? (
                    <Button
                      ref={(el: HTMLButtonElement | null) => {
                        if (el) {
                          showChangesRefs.current.set(version.version, el)
                        } else {
                          showChangesRefs.current.delete(version.version)
                        }
                      }}
                      aria-label={`Show changes in version ${version.version}`}
                      onClick={() => handleShowChanges(version)}
                    >
                      Show changes
                    </Button>
                  ) : null}
                  {isOwner ? (
                    <Button onClick={handleRestore(version)} disabled={loading || version.version === entity.version}>
                      Restore
                    </Button>
                  ) : null}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </>
      ) : null}
    </>
  )
}

export default EntityHistory
